import { Box, Grid, Tab, Tabs, useMediaQuery } from '@mui/material'
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import VehicleService from '../../../../shared/services/vehicle/vehicle.service';
import { IVehicleDetails } from '../../../../shared/services/vehicle/vehicle.model';
import gaiaTheme from '../../../../root-components/theme';
import { a11yProps } from '../../../../shared/utils/misc-shared-utils';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { LoadingPage } from '../../loading-page';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { SearchBar } from '../../../presentation-components/display-elements-components/search-bar';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { useDebounce } from '../../../../hooks/use-debounce-hook';

export const OrganisationVehiclesPage = () => {
  const navigate = useNavigate();
  //Services
  const vehicleService = VehicleService.getInstance();
  //Context
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  //State
  const [vehicles, setVehicles] = useState<IVehicleDetails[]>()
  const [value, setValue] = React.useState(0);
  const [searchQuery, setSearchQuery] = useState('')
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Hooks
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  //Ref
  const requestId = useRef<number>(0)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  //Use Effects
  useEffect(() => {
    requestId.current++
    const currentRequestId = requestId.current
    vehicleService.getAllVehiclesWithPagination(value === 0 ? true : false, debouncedSearchQuery, inputPage, perPage, orderBy, order)
      .then(response => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
          setTotalRecords(response.count);
          setVehicles(response.rows)
        }
      })
      .catch(err => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
        }
        console.log(err)
      })
  }, [inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [value, order, orderBy, debouncedSearchQuery])

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'customUniqueId', numeric: false, label: `${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Registration`, sortable: true, isHeading: true },
    { id: 'vehicleReference', numeric: false, label: 'Reference(s)', sortable: true },
    { id: 'vehicleType', numeric: false, label: `${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Type`, sortable: true },
    ...(userDetails?.organisation?.enableSubsidiaries
      ? [{ id: 'subsidiary', numeric: false, label: `Default ${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}`, sortable: true }]
      : []),
  ];

  const rows = !vehicles ? [] : vehicles.map(vehicle => {
    return {
      id: vehicle.id,
      customUniqueId: vehicle.customUniqueId,
      vehicleReference: vehicle.vehicleReference,
      vehicleType: vehicle.vehicleType,
      subsidiary: vehicle.subsidiary.name,
    }
  });

  // Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const handleSelected = (selectedRowId: string[]) => {
    if (selectedRowId[0] !== undefined) {
      navigate(`/organisation/vehicles/${selectedRowId[0]}`)
    }
  }

  // Component Event Handlers
  const changeTabHandler = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setIsLoading(true)
    setSearchQuery('')
  };

  return (
    <PageOuterContainer>
      <PageHeading
        title={screenLabelsContext.getLabel('vehicle', { plural: true, capitalize: 'first' })}
        hasTabs={true}
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => navigate('/organisation/vehicles/add-new-vehicle')}
        actionButtonText={`Add ${screenLabelsContext.getLabel('vehicle', { plural: false })}`}
        includeSecondaryActionButton={true}
        secondaryActionButtonIcon={IconName.UploadOutlined}
        secondaryActionButtonHandler={() => navigate('/settings/data-upload/import-data?dataType=vehicles&start=upload')}
        secondaryActionButtonText='Upload Data'
        hideShowSecondaryActionButton={!userDetails?.roles.includes('admin')}
      />
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={changeTabHandler} aria-label="basic tabs example">
            <Tab label="Active" {...a11yProps(0)} />
            <Tab label="Inactive" {...a11yProps(1)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <Grid container sx={{ mb: 2 }}>
            <Grid
              item
              xl={4} lg={4} md={6} sm={12} xs={12}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                width: '100%'
              }}>
              <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            </Grid>
          </Grid>
          <LoadingPage pageReady={!isLoading}>
            {vehicles &&
              <TableDisplay
                onOrderChange={handleOrderChange}
                isMobileMode={isMobileMode}
                headCells={headCells}
                rows={rows}
                handleSelected={handleSelected}
                selected={selected}
                setSelected={setSelected}
                inputPage={inputPage}
                setInputPage={setInputPage}
                totalRecords={totalRecords}
                perPage={perPage}
                setPerPage={setPerPage} />}
          </LoadingPage>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <Grid container sx={{ mb: 2 }}>
            <Grid item xl={4} lg={4} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', width: '100%' }}>
              <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            </Grid>
          </Grid>
          <LoadingPage pageReady={!isLoading}>
            {vehicles &&
              <TableDisplay
                onOrderChange={handleOrderChange}
                isMobileMode={isMobileMode}
                headCells={headCells}
                rows={rows}
                handleSelected={handleSelected}
                selected={selected}
                setSelected={setSelected}
                inputPage={inputPage}
                setInputPage={setInputPage}
                totalRecords={totalRecords}
                perPage={perPage}
                setPerPage={setPerPage} />}
          </LoadingPage>
        </CustomTabPanel>
      </Box>
    </PageOuterContainer>
  )
}